import styled from 'styled-components';

import { Text } from '@/nova/components';
import { COLORS, ANIM } from '@/nova/theme/tokens';

/* ── the pool picker (a Sheet body): a responsive grid of pickable pool banners ───────────────────── */

export const PickerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;

/* One pickable pool banner: artwork on top, title BENEATH it, and a solid check badge in the bottom bar
   when it is already selected. Disabled (past the max, and not itself selected) reads as dimmed + inert. */
export const PickerCard = styled.button<{ $selected?: boolean; $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 0;
  text-align: left;
  border-radius: 14px;
  overflow: hidden;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
  background: ${COLORS.surfaceFaint};
  border: 2px solid ${({ $selected }) => ($selected ? COLORS.ctaBorder : COLORS.hairline)};
  transition:
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    border-color: ${({ $selected, $disabled }) =>
      $disabled ? COLORS.hairline : $selected ? COLORS.ctaBorder : COLORS.textFaint};
  }
`;

export const PickerArt = styled.div`
  position: relative;
  aspect-ratio: 2 / 1;
  background: ${COLORS.surfaceInset};
`;

export const PickerImg = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/* "Selected" check — sits in the card's bottom bar (right of the title), not over the artwork. */
export const PickCheck = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  color: ${COLORS.ctaSolidText};
  background: ${COLORS.ctaSolid};
`;

/* The picker card's bottom bar: the title on the left, the selected-check on the right. */
export const PickerFoot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px 11px;
`;

export const PickerTitle = styled(Text).attrs({ tag: 'span' as const })`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 650;
  color: ${COLORS.heading};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* An optional one-line hint above the grid (e.g. "Pick up to N, first is the key visual"). */
export const PickerHint = styled(Text).attrs({ tag: 'paragraph' as const })`
  margin: 0 0 14px;
  font-size: 13px;
  color: ${COLORS.textMuted};
`;

export const PickerEmpty = styled(Text).attrs({ tag: 'paragraph' as const })`
  margin: 0;
  padding: 32px 8px;
  text-align: center;
  font-size: 14px;
  color: ${COLORS.textMuted};
`;
